import React, { useEffect, useState, useRef, useCallback } from 'react';
import { X, RefreshCw } from 'lucide-react';
import { t } from '../utils/i18n';
import './UpdateBanner.css';

const SHOW_DURATION_MS = 12 * 1000;       // visible for 12s per cycle
const REMIND_INTERVAL_MS = 2 * 60 * 1000; // re-show every 2 minutes

/**
 * Update banner — listens for `sw-update-available` CustomEvent.
 *
 * Cycle:
 *   visible 12s (with subtle pulse) → hidden 2 min → re-visible 12s → ...
 *
 * Stops permanently when:
 *   • User clicks "Refresh" → page reloads (implicit).
 *   • User clicks the ✕ → in-memory `dismissed` flag (this session only).
 */
const UpdateBanner = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const hideTimerRef = useRef(null);
  const remindTimerRef = useRef(null);

  const clearTimers = () => {
    if (hideTimerRef.current) { clearTimeout(hideTimerRef.current); hideTimerRef.current = null; }
    if (remindTimerRef.current) { clearTimeout(remindTimerRef.current); remindTimerRef.current = null; }
  };

  // Listen for the service-worker update signal
  useEffect(() => {
    const handler = () => setUpdateAvailable(true);
    window.addEventListener('sw-update-available', handler);
    return () => window.removeEventListener('sw-update-available', handler);
  }, []);

  const showCycle = useCallback(() => {
    setVisible(true);
    hideTimerRef.current = setTimeout(() => {
      setVisible(false);
      remindTimerRef.current = setTimeout(showCycle, REMIND_INTERVAL_MS);
    }, SHOW_DURATION_MS);
  }, []);

  // Start the visibility cycle once the update is signalled (and not dismissed)
  useEffect(() => {
    if (!updateAvailable || dismissed) return;
    showCycle();
    return clearTimers;
  }, [updateAvailable, dismissed, showCycle]);

  if (!updateAvailable || dismissed) return null;

  const handleRefresh = () => {
    clearTimers();
    window.location.reload();
  };

  const handleClose = () => {
    clearTimers();
    setDismissed(true);
  };

  return (
    <div
      className={`update-banner ${visible ? 'is-visible' : 'is-hidden'}`}
      role="status"
      aria-live="polite"
      data-testid="update-banner"
    >
      <span className="update-banner-text">{t('updateAvailable')}</span>
      <button
        type="button"
        className="update-banner-refresh"
        onClick={handleRefresh}
        data-testid="update-banner-refresh"
      >
        <RefreshCw size={14} />
        {t('updateRefresh')}
      </button>
      <button
        type="button"
        className="update-banner-close"
        onClick={handleClose}
        data-testid="update-banner-close"
        aria-label="Close"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default UpdateBanner;
